import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { IAuditListItem } from 'src/types/audit';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  audits: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IAuditListItem[];
  };
  employeeAuditLog: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IAuditListItem[];
  };
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  audits: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  employeeAuditLog: {
    status: 'idle',
    error: undefined,
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
};

const auditSlice = createSlice({
  name: 'audit',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getPaginatedAudits.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPaginatedAudits.fulfilled, (state, action) => {
        state.status = 'success';
        state.audits = action.payload;
      })
      .addCase(getPaginatedAudits.rejected, (state, action) => {
        state.audits = initialState.audits;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getEmployeeAuditLog.pending, (state) => {
        state.employeeAuditLog.status = 'loading';
      })
      .addCase(getEmployeeAuditLog.fulfilled, (state, action) => {
        state.employeeAuditLog.status = 'success';
        state.employeeAuditLog.count = action.payload.count;
        state.employeeAuditLog.page = action.payload.page;
        state.employeeAuditLog.pageSize = action.payload.pageSize;
        state.employeeAuditLog.pageCount = action.payload.pageCount;
        state.employeeAuditLog.items = action.payload.items;
      })
      .addCase(getEmployeeAuditLog.rejected, (state, action) => {
        state.employeeAuditLog.status = 'failure';
        state.employeeAuditLog.error = action.error.message;
      });
  },
});

export default auditSlice.reducer;

export const getPaginatedAudits = createAsyncThunk(
  'audit/paginated',
  async (params: {
    page: number | null;
    limit: number | null;
    generalSearch?: string | null;
    type?: string | null;
    action?: string | null;
    actorId?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
    sortBy: string | null;
    sort: 'asc' | 'desc' | null;
  }) => {
    try {
      const response = await axios.get(
        `audit?page=${params.page !== null ? params.page + 1 : ''}&limit=${
          params.limit ?? ''
        }&generalSearch=${params.generalSearch ?? ''}&type=${params.type ?? ''}&action=${
          params.action ?? ''
        }&actorId=${params.actorId ?? ''}&startDate=${params.startDate ?? ''}&endDate=${
          params.endDate ?? ''
        }&sortBy=${params.sortBy}&sort=${params.sort}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getEmployeeAuditLog = createAsyncThunk(
  'audit/employee',
  async (params: { employeeId: string; page: number; limit: number }) => {
    try {
      const response = await axios.get(
        `audit/employee/${params.employeeId}?page=${params.page}&limit=${params.limit}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);
